import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { datePickerPopperProps } from "../../components/admin/datePickerPopper";

interface HistoryFiltersProps {
  initialFilters: FilterState;
  closeDropdown: () => void;
  onApplyFilters: (filters: FilterState) => void;
}

interface FilterState {
  requestedFromDate?: string;
  requestedToDate?: string;
  answeredFromDate?: string;
  answeredToDate?: string;
  status?: string;
}

// Format Date as local YYYY-MM-DD
const toYMD = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "Answered", label: "Answered" },
  { value: "Expired", label: "Expired" },
];

// Parse YYYY-MM-DD string into a local Date
const parseYMD = (s?: string): Date | null =>
  s ? new Date(`${s}T00:00:00`) : null;

const HistoryFilters: React.FC<HistoryFiltersProps> = ({
  initialFilters,
  closeDropdown,
  onApplyFilters,
}) => {
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const statusMenuRef = useRef<HTMLDivElement | null>(null);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(null);

  // The menu lives in a portal, so a click inside it lands outside statusRef —
  // check both before closing.
  useEffect(() => {
    if (!isStatusOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (statusRef.current?.contains(target)) return;
      if (statusMenuRef.current?.contains(target)) return;
      setIsStatusOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [isStatusOpen]);

  // Position the portal menu under the trigger, flipping above when there is
  // not enough room below.
  useEffect(() => {
    if (!isStatusOpen) {
      setMenuRect(null);
      return;
    }
    const place = () => {
      const el = statusRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const MENU_HEIGHT = 130;
      const below = window.innerHeight - rect.bottom;
      const top =
        below < MENU_HEIGHT + 8 ? rect.top - MENU_HEIGHT - 6 : rect.bottom + 6;
      setMenuRect({ top, left: rect.left, width: rect.width });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [isStatusOpen]);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  const handleInputChange = (field: keyof FilterState, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateChange = (
    field:
      | "requestedFromDate"
      | "requestedToDate"
      | "answeredFromDate"
      | "answeredToDate",
    date: Date | null
  ) => {
    setFilters((prev) => ({
      ...prev,
      [field]: date ? toYMD(date) : "",
    }));
  };

  const clearAllFilters = () => {
    const empty: FilterState = {
      requestedFromDate: "",
      requestedToDate: "",
      answeredFromDate: "",
      answeredToDate: "",
      status: "",
    };
    setFilters(empty);
    onApplyFilters(empty);
    closeDropdown();
  };

  const handleApply = () => {
    onApplyFilters(filters);
    closeDropdown();
  };

  return (
    <div className="bg-white divide-y divide-gray-100 rounded-lg shadow-2xl border border-gray-200 w-full max-h-[calc(100vh-7rem)] overflow-y-auto">
      <div className="px-3 text-sm text-gray-700" aria-labelledby="dropdownDefaultButton-3">
        {/* Requested Date */}
        <div className="bg-indigo-200 bg-opacity-25 rounded-lg mt-3 p-3 mb-3">
          <p className="text-sm font-medium text-grease-100">Requested Date</p>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="relative">
              <DatePicker
                selected={parseYMD(filters.requestedFromDate)}
                onChange={(date: Date | null) =>
                  handleDateChange("requestedFromDate", date)
                }
                selectsStart
                startDate={parseYMD(filters.requestedFromDate)}
                endDate={parseYMD(filters.requestedToDate)}
                maxDate={parseYMD(filters.requestedToDate) || new Date()}
                placeholderText="From Date"
                dateFormat="dd/MM/yyyy"
                onKeyDown={(e) => e.preventDefault()}
                wrapperClassName="w-full"
                className="block w-full p-2.5 pr-8 rounded-xl bg-white border border-[#ECECED] text-sm font-semibold text-[#192227] placeholder-[#9CA3AF] focus:outline-none focus:border-[#FFBC00] focus:ring-1 focus:ring-[#FFBC00]"
                calendarStartDay={1}
                calendarClassName="st-history-datepicker"
                {...datePickerPopperProps}
                popperPlacement="bottom-start"
              />
              <i className="st-calendar text-lightgray-200 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-xs"></i>
            </div>
            <div className="relative">
              <DatePicker
                selected={parseYMD(filters.requestedToDate)}
                onChange={(date: Date | null) =>
                  handleDateChange("requestedToDate", date)
                }
                selectsEnd
                startDate={parseYMD(filters.requestedFromDate)}
                endDate={parseYMD(filters.requestedToDate)}
                minDate={parseYMD(filters.requestedFromDate) || undefined}
                maxDate={new Date()}
                placeholderText="To Date"
                dateFormat="dd/MM/yyyy"
                onKeyDown={(e) => e.preventDefault()}
                wrapperClassName="w-full"
                className="block w-full p-2.5 pr-8 rounded-xl bg-white border border-[#ECECED] text-sm font-semibold text-[#192227] placeholder-[#9CA3AF] focus:outline-none focus:border-[#FFBC00] focus:ring-1 focus:ring-[#FFBC00]"
                calendarStartDay={1}
                calendarClassName="st-history-datepicker"
                {...datePickerPopperProps}
                popperPlacement="bottom-end"
              />
              <i className="st-calendar text-lightgray-200 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-xs"></i>
            </div>
          </div>
        </div>

        {/* Answered Date */}
        <div className="bg-indigo-200 bg-opacity-25 rounded-lg mt-3 p-3 mb-3">
          <p className="text-sm font-medium text-grease-100">Answered Date</p>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="relative">
              <DatePicker
                selected={parseYMD(filters.answeredFromDate)}
                onChange={(date: Date | null) =>
                  handleDateChange("answeredFromDate", date)
                }
                selectsStart
                startDate={parseYMD(filters.answeredFromDate)}
                endDate={parseYMD(filters.answeredToDate)}
                maxDate={parseYMD(filters.answeredToDate) || new Date()}
                placeholderText="From Date"
                dateFormat="dd/MM/yyyy"
                onKeyDown={(e) => e.preventDefault()}
                wrapperClassName="w-full"
                className="block w-full p-2.5 pr-8 rounded-xl bg-white border border-[#ECECED] text-sm font-semibold text-[#192227] placeholder-[#9CA3AF] focus:outline-none focus:border-[#FFBC00] focus:ring-1 focus:ring-[#FFBC00]"
                calendarStartDay={1}
                calendarClassName="st-history-datepicker"
                {...datePickerPopperProps}
                popperPlacement="bottom-start"
              />
              <i className="st-calendar text-lightgray-200 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-xs"></i>
            </div>
            <div className="relative">
              <DatePicker
                selected={parseYMD(filters.answeredToDate)}
                onChange={(date: Date | null) =>
                  handleDateChange("answeredToDate", date)
                }
                selectsEnd
                startDate={parseYMD(filters.answeredFromDate)}
                endDate={parseYMD(filters.answeredToDate)}
                minDate={parseYMD(filters.answeredFromDate) || undefined}
                maxDate={new Date()}
                placeholderText="To Date"
                dateFormat="dd/MM/yyyy"
                onKeyDown={(e) => e.preventDefault()}
                wrapperClassName="w-full"
                className="block w-full p-2.5 pr-8 rounded-xl bg-white border border-[#ECECED] text-sm font-semibold text-[#192227] placeholder-[#9CA3AF] focus:outline-none focus:border-[#FFBC00] focus:ring-1 focus:ring-[#FFBC00]"
                calendarStartDay={1}
                calendarClassName="st-history-datepicker"
                {...datePickerPopperProps}
                popperPlacement="bottom-end"
              />
              <i className="st-calendar text-lightgray-200 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-xs"></i>
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="bg-indigo-200 bg-opacity-25 rounded-lg mt-3 p-3 mb-3 border-b border-lightgray-100">
          <p className="text-sm font-medium text-grease-100">Select Status</p>
          {/* Collapsed trigger + popup list, styled like the Loads filter dropdown. */}
          <div className="relative w-full mt-2" ref={statusRef}>
            <button
              type="button"
              onClick={() => setIsStatusOpen((prev) => !prev)}
              className="flex w-full items-center justify-between gap-2 bg-white border border-[#ECECED] rounded-xl px-3 py-2.5 text-sm font-semibold text-[#192227] hover:bg-gray-50 transition-colors"
            >
              <span>{STATUS_OPTIONS.find((o) => o.value === (filters.status || ""))?.label}</span>
              <i className={`st-arrow-down text-[10px] text-[#6B7280] transition-transform ${isStatusOpen ? "rotate-180" : ""}`}></i>
            </button>
            {/* Rendered in a portal with fixed positioning: the filter panel
                clips its children (overflow-y-auto), so an in-panel menu would
                either be cut off or force the panel to grow. */}
            {isStatusOpen && menuRect &&
              createPortal(
            <div
              ref={statusMenuRef}
              className="st-status-menu font-sans fixed z-[60] bg-white rounded-xl shadow-xl border border-gray-200/60 px-3 py-2.5 flex flex-col gap-2"
              style={{ top: menuRect.top, left: menuRect.left, width: menuRect.width }}
            >
              {STATUS_OPTIONS.map((o) => {
                const isSelected = (filters.status || "") === o.value;
                return (
                  <button
                    key={o.label}
                    type="button"
                    onClick={() => {
                      handleInputChange("status", o.value);
                      setIsStatusOpen(false);
                    }}
                    className="flex items-center gap-2 py-0.5 text-left group cursor-pointer transition-colors"
                  >
                    <span
                      className={`flex-shrink-0 w-[18px] h-[18px] rounded-full border-[1.8px] flex items-center justify-center transition-all bg-white ${
                        isSelected
                          ? "border-black"
                          : "border-[#9CA3AF] group-hover:border-gray-500"
                      }`}
                    >
                      {isSelected && <span className="w-2.5 h-2.5 rounded-full bg-[#FFBC00]" />}
                    </span>
                    <span
                      className={`text-sm font-semibold transition-colors ${
                        isSelected
                          ? "text-black"
                          : "text-[#192227] group-hover:text-black"
                      }`}
                    >
                      {o.label}
                    </span>
                  </button>
                );
              })}
            </div>,
            document.body
          )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={closeDropdown}
            className="flex gap-2 px-3 py-2 bg-blue-500 bg-opacity-25 rounded-md"
          >
            <p className="font-medium text-lightgray-200 text-xs">Cancel</p>
          </button>
          <div className="flex items-center gap-4">
            <button
              onClick={clearAllFilters}
              className="flex gap-2 px-3 py-2 border border-[#FFBC00] rounded-md"
            >
              <p className="font-medium text-[#FFBC00] text-xs">Clear All</p>
            </button>
            <button
              onClick={handleApply}
              className="flex gap-2 px-3 py-2 bg-[#FFBC00] rounded-md"
            >
              <p className="font-medium text-black text-xs">Apply</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryFilters;
